<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useUserStore } from '@/store/useUserStore'
import { usePresenceStore } from '@/store/usePresenceStore'
import { useStorage } from '@/composables/useStorage'
import { useCongregationInvites } from '@/composables/useCongregationInvites'
import { usePublicTheme } from '@/composables/usePublicTheme'

const themeStore = usePublicTheme()
const route = useRouterStore()
const userStore = useUserStore()
const presenceStore = usePresenceStore()
const { getData } = useStorage()
const { validateInvite, completeInvite } = useCongregationInvites()

const token = computed(() => route.params.token)

const loading = ref(true)
const errorMsg = ref(null)
const valid = ref(false)

const form = ref({ congregationName: '', email: '', password: '', passwordConfirm: '' })
const submitting = ref(false)
const formError = ref(null)

const load = async () => {
  loading.value = true
  errorMsg.value = null
  if (!token.value) {
    errorMsg.value = 'Enlace inválido: falta el token.'
    loading.value = false
    return
  }
  const res = await validateInvite(token.value)
  if (res.error) {
    errorMsg.value = res.error === 'Este enlace ya fue utilizado'
      ? 'Este enlace ya fue utilizado.'
      : 'Este enlace no es válido o fue eliminado.'
  } else {
    valid.value = true
    document.title = 'Registrar congregación'
  }
  loading.value = false
}

const submit = async () => {
  formError.value = null
  if (!form.value.congregationName.trim()) {
    formError.value = 'Indicá el nombre de la congregación.'
    return
  }
  if (!form.value.email.trim() || !form.value.password) {
    formError.value = 'Email y contraseña son requeridos.'
    return
  }
  if (form.value.password.length < 6) {
    formError.value = 'La contraseña debe tener al menos 6 caracteres.'
    return
  }
  if (form.value.password !== form.value.passwordConfirm) {
    formError.value = 'Las contraseñas no coinciden.'
    return
  }

  submitting.value = true
  try {
    const result = await completeInvite(token.value, {
      congregationName: form.value.congregationName.trim(),
      email: form.value.email.trim(),
      password: form.value.password,
    })
    if (result.error) {
      formError.value = result.error
      valid.value = result.error !== 'Este enlace ya fue utilizado'
      return
    }
    userStore.setUser(result)
    // getData() decide la página final (una congregación nueva sin territorios
    // va a 'parametros', igual que cualquier usuario nuevo): hay que esperarla
    // para no pisarla con una navegación propia en paralelo.
    await getData()
    if (result.congregationId) presenceStore.connect()
  } finally {
    submitting.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="simple-page">
    <button
      type="button"
      class="theme-toggle-btn"
      :title="themeStore.theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
      @click="themeStore.toggle()"
    >
      <i :class="themeStore.theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" />
    </button>

    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="!valid" class="simple-error">{{ errorMsg }}</div>

    <template v-else>
      <h1 class="simple-title">Registrar congregación</h1>
      <div class="simple-hint">
        Completá los datos de tu congregación y tu usuario. Vas a quedar como su primer administrador.
      </div>

      <form class="simple-form" @submit.prevent="submit">
        <label class="simple-field-label" for="cong-nombre">Nombre de la congregación</label>
        <input
          id="cong-nombre"
          v-model="form.congregationName"
          type="text"
          class="simple-input"
          placeholder="Ej: Congregación Norte"
        >

        <label class="simple-field-label mt" for="cong-email">Tu email</label>
        <input
          id="cong-email"
          v-model="form.email"
          type="email"
          class="simple-input"
          placeholder="tu@email.com"
        >

        <label class="simple-field-label mt" for="cong-pass">Contraseña</label>
        <input
          id="cong-pass"
          v-model="form.password"
          type="password"
          class="simple-input"
          placeholder="Mínimo 6 caracteres"
        >

        <label class="simple-field-label mt" for="cong-pass2">Confirmar contraseña</label>
        <input
          id="cong-pass2"
          v-model="form.passwordConfirm"
          type="password"
          class="simple-input"
          placeholder="Repetí la contraseña"
        >

        <div v-if="formError" class="simple-error-msg">{{ formError }}</div>

        <button type="submit" class="simple-btn" :disabled="submitting">
          {{ submitting ? 'Creando...' : 'Crear congregación' }}
        </button>
      </form>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.theme-toggle-btn {
  position: fixed;
  top: max(env(safe-area-inset-top, 0px), 16px);
  right: 16px;
  z-index: 10;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid var(--separator, rgba(0, 0, 0, 0.15));
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #666);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-toggle-btn:active {
  opacity: 0.7;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 16px;
}

.simple-hint {
  width: 100%;
  max-width: 420px;
  background: var(--info-soft, #e6f2ff);
  color: var(--label-primary, #333);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 4px;
  margin-bottom: 12px;
  text-align: left;
}

.simple-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  text-align: left;
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-field-label.mt {
  margin-top: 22px;
}

.simple-input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-btn {
  margin-top: 20px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
